/**
 * MUTAGIM.SHOP — Product Reviews Component
 * Affiche les avis clients sur la page produit
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, ThumbsUp, CheckCircle, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating, size = 'md' }) {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            sizeClasses[size],
            star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
          )}
        />
      ))}
    </div>
  );
}

export function ProductReviews({ productSlug, productStats }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'fr';
  
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    average: 0,
    distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 }
  });
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchReviews(1);
  }, [productSlug]);

  const fetchReviews = async (page = 1) => {
    try {
      if (page > 1) setLoadingMore(true);
      
      const response = await axios.get(`${API_URL}/api/products/${productSlug}/reviews?page=${page}&limit=10`);
      const newReviews = response.data.reviews || [];
      
      if (page === 1) {
        setReviews(newReviews);
      } else {
        setReviews(prev => [...prev, ...newReviews]);
      }
      
      setStats({
        total: response.data.total || 0,
        average: response.data.average_rating || productStats?.average_rating || 0,
        distribution: response.data.rating_distribution || {}
      });
      
      setCurrentPage(page);
      setHasMore(page < (response.data.pages || 1));
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  const loadMoreReviews = async () => {
    if (!loadingMore && hasMore) {
      await fetchReviews(currentPage + 1);
    }
  };

  // Calculer la note moyenne depuis la distribution si pas fournie
  const calculateAverage = () => {
    if (stats.average > 0) return stats.average;
    
    let totalStars = 0;
    let totalReviews = 0;
    
    Object.entries(stats.distribution).forEach(([stars, count]) => {
      totalStars += parseInt(stars) * count;
      totalReviews += count;
    });
    
    return totalReviews > 0 ? (totalStars / totalReviews).toFixed(1) : 0;
  };

  const averageRating = calculateAverage();
  const displayedReviews = showAll ? reviews : reviews.slice(0, 3);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-zinc-800 rounded w-1/3"></div>
        <div className="h-24 bg-zinc-800 rounded"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null; // Ne pas afficher la section si pas d'avis
  }

  return (
    <div className="mt-12 border-t border-zinc-800 pt-8" data-testid="product-reviews">
      {/* Header */}
      <h2 className="text-2xl font-bold mb-6">
        {t('reviews.title', 'Avis clients')}
      </h2>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {/* Average Rating */}
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-5xl font-bold text-white">{averageRating}</div>
            <StarRating rating={Math.round(averageRating)} size="lg" />
            <div className="text-sm text-gray-400 mt-1">
              {stats.total} {t('reviews.reviews', 'avis')}
            </div>
          </div>
          
          {/* Distribution */}
          <div className="flex-1 space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => {
              const count = stats.distribution[String(stars)] || 0;
              const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
              
              return (
                <div key={stars} className="flex items-center gap-2 text-sm">
                  <span className="w-3 text-gray-400">{stars}</span>
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <Progress value={percentage} className="h-2 flex-1 bg-zinc-800" />
                  <span className="w-8 text-right text-gray-400">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-6">
        {displayedReviews.map((review) => (
          <div
            key={review.id}
            className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800"
            data-testid={`review-${review.id}`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{review.author_name}</span>
                  {review.verified_purchase && (
                    <span className="flex items-center gap-1 text-xs text-green-500">
                      <CheckCircle className="h-3 w-3" />
                      {t('reviews.verified', 'Achat vérifié')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <StarRating rating={review.rating} size="sm" />
                  <span className="text-sm text-gray-400">
                    {new Date(review.created_at).toLocaleDateString(lang === 'he' ? 'he-IL' : lang === 'en' ? 'en-US' : 'fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Title */}
            {review.title && (
              <h4 className="font-medium mb-2">{review.title}</h4>
            )}

            {/* Content */}
            <p className="text-gray-300 leading-relaxed">
              {(() => {
                // Récupérer le contenu dans la langue appropriée
                let text = review.content?.[lang] || review.content?.fr || review.content?.en || '';
                // Si le contenu commence par le titre (doublon), le supprimer
                if (review.title && text.startsWith(review.title)) {
                  text = text.substring(review.title.length).trim();
                  // Supprimer le saut de ligne au début si présent
                  if (text.startsWith('\n')) {
                    text = text.substring(1).trim();
                  }
                }
                return text;
              })()}
            </p>

            {/* Helpful */}
            {review.helpful_count > 0 && (
              <div className="flex items-center gap-2 mt-4 text-sm text-gray-400">
                <ThumbsUp className="h-4 w-4" />
                <span>
                  {review.helpful_count} {t('reviews.helpful', 'personnes ont trouvé cet avis utile')}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Show More Button */}
      {reviews.length > 3 && !showAll && (
        <div className="text-center mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAll(true)}
            className="border-zinc-700"
          >
            <ChevronDown className="h-4 w-4 mr-2" />
            {t('reviews.showAll', 'Voir tous les avis')} ({stats.total})
          </Button>
        </div>
      )}
      
      {/* Load More Button - when showAll is true and there are more reviews to load */}
      {showAll && hasMore && (
        <div className="text-center mt-6">
          <Button
            variant="outline"
            onClick={loadMoreReviews}
            disabled={loadingMore}
            className="border-zinc-700"
          >
            {loadingMore ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                {t('reviews.loading', 'Chargement...')}
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-2" />
                {t('reviews.loadMore', 'Charger plus d\'avis')} ({reviews.length}/{stats.total})
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default ProductReviews;
